import { toast } from "sonner";
import { API_ENDPOINTS, HTTP_METHODS } from "../config/api";
import { ApiResponse, BaseApiService } from "./baseApi";

// Represents a single combined tracking record from the API response (flat array)
export interface RawTrackingRecord {
  heatNo: string;
  grnDetailId: number;
  grnReceivedQuantity: number;
  grnQualityCheck: string | null;
  supplierHeatNo: string;
  tcNo: string;
  tcDate: string | null;
  grnReceivedDate: string;
  grnId: number;
  grnNumber: string;
  grnReceiveDate: string;
  grnBranchId: number;
  grnBranchName: string;
  rawItemId: number;
  rawItemName: string;
  rawItemSku: string;
  workOrderId: number;
  workOrderNumber: string;
  workOrderStatus: string;
  completedQuantity: number;
  rejectQuantity: number;
  workOrderCreatedDate: string;
  workOrderBranchId: number;
  workOrderBranchName: string;
  endgoodId: number;
  endgoodName: string;
  endgoodSku: string;
  materialIssueId: number;
  issueQuantity: number;
  issueDate: string;
  materialIssueHeatNumber: string;
  workOrderOperationId: number;
  operationName: string;
  productionDate: string;
  operationCompletedQuantity: number;
  operationRejectQuantity: number;
  packingHeaderId: number;
  packingHeaderNo: string;
  packingBatchId: number;
  packingDate: string;
  packingStatus: string;
  packingDetailId: number;
  boxNumber: string;
  boxQuantity: number;
  boxStatus: string;
  isInvoiced: boolean;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  salesOrderId: number;
  salesOrderNumber: string;
  customerId: number;
  customerName: string;
  customerCode: string;
}

// DTOs for the structured output for display
export interface HeatDetails {
  heatNo: string;
  grnInfo: {
    grnId: number;
    grnNumber: string;
    grnReceivedDate: string;
    grnReceivedQuantity: number;
    grnQualityCheck: string | null;
    supplierHeatNo: string;
    tcNo: string;
    tcDate: string | null;
    rawItemName: string;
    rawItemSku: string;
  };
  associatedWorkOrders: {
    workOrderId: number;
    workOrderNumber: string;
    workOrderStatus: string;
    completedQuantity: number;
    rejectQuantity: number;
    workOrderCreatedDate: string;
    operations: {
      operationName: string;
      productionDate: string;
      operationCompletedQuantity: number;
      operationRejectQuantity: number;
    }[];
  }[];
  associatedBoxes: {
    boxNumber: string;
    boxQuantity: number;
    boxStatus: string;
    packingHeaderNo: string;
    packingDate: string;
    packingStatus: string;
    isInvoiced: boolean;
    invoiceNumber: string | null;
    invoiceDate: string | null;
    salesOrderNumber: string;
    customerName: string;
  }[];
}

export interface WorkOrderDetails {
  workOrderNumber: string;
  workOrderId: number;
  workOrderStatus: string;
  completedQuantity: number;
  rejectQuantity: number;
  workOrderCreatedDate: string;
  endgoodName: string;
  endgoodSku: string;
  associatedHeatNumbers: {
    heatNo: string;
    materialIssueDate: string;
    issueQuantity: number;
  }[];
  operations: {
    operationName: string;
    productionDate: string;
    operationCompletedQuantity: number;
    operationRejectQuantity: number;
  }[];
  associatedBoxes: {
    boxNumber: string;
    boxQuantity: number;
    boxStatus: string;
    packingHeaderNo: string;
    packingDate: string;
    isInvoiced: boolean;
    invoiceNumber: string | null;
    invoiceDate: string | null;
  }[];
}

export interface BoxDetails {
  boxNumber: string;
  boxQuantity: number;
  boxStatus: string;
  packingHeaderNo: string;
  packingDate: string;
  packingStatus: string;
  isInvoiced: boolean;
  invoiceNumber: string | null;
  invoiceDate: string | null;
  salesOrderNumber: string;
  customerName: string;
  associatedHeatNumbers: {
    heatNo: string;
    rawItemName: string;
    materialIssueDate: string;
  }[];
  associatedWorkOrders: {
    workOrderNumber: string;
    endgoodName: string;
    completedQuantity: number;
    rejectQuantity: number;
  }[];
}

/** Structured response from GET /tracking/by-invoice-number */
export interface InvoiceTrackingHeatSource {
  heatNo?: string | null;
  grnId?: number | null;
  grnNumber?: string | null;
  poId?: number | null;
  poNumber?: string | null;
  supplierName?: string | null;
  supplierHeatNo?: string | null;
  receiveDate?: string | null;
  dcNo?: string | null;
  rawItemId?: number | null;
  rawItemName?: string | null;
  receivedQuantity?: number | null;
  remainingQuantity?: number | null;
}

export interface InvoiceTrackingMaterialIssue {
  materialIssueId?: number | null;
  issueQuantity?: number | null;
  issueDate?: string | null;
  status?: string | null;
  issueType?: string | null;
  heatNumber?: string | null;
  rawItemId?: number | null;
  rawItemName?: string | null;
  endgoodId?: number | null;
  endgoodName?: string | null;
  heatSources?: InvoiceTrackingHeatSource[];
}

export interface InvoiceTrackingWorkOrder {
  workOrderId?: number | null;
  workOrderNumber?: string | null;
  status?: string | null;
  quantity?: number | null;
  completedQuantity?: number | null;
  rejectQuantity?: number | null;
  endgoodId?: number | null;
  endgoodName?: string | null;
  branchId?: number | null;
  /** Distinct heat numbers from material_issue for this work order. */
  heatNumbers?: string[];
  materialIssues?: InvoiceTrackingMaterialIssue[];
}

export interface InvoiceTrackingBox {
  packingDetailId?: number | null;
  boxNumber?: string | null;
  quantity?: number | null;
  status?: string | null;
  packingBatchId?: string | null;
  packingHeaderId?: number | null;
  packingHeaderNo?: string | null;
  packingDate?: string | null;
  packingStatus?: string | null;
  workOrderId?: number | null;
  workOrderNumber?: string | null;
  materialIssueHeatNumbers?: string[];
}

/** WO refs on an invoice line (from linked boxes / packing headers). */
export interface InvoiceTrackingLineWorkOrderRef {
  workOrderId?: number | null;
  workOrderNumber?: string | null;
  boxCount?: number;
  materialIssueHeatNumbers?: string[];
}

export interface InvoiceTrackingLine {
  invoiceDetailId?: number | null;
  lineItemType?: string | null;
  endgoodId?: number | null;
  endgoodName?: string | null;
  rawItemId?: number | null;
  rawItemName?: string | null;
  qty?: number | null;
  uom?: string | null;
  rate?: number | null;
  hsn?: string | null;
  heatNo?: string | null;
  userProvidedHeatNo?: string | null;
  /** Legacy single WO — prefer linkedWorkOrders. */
  workOrderId?: number | null;
  workOrderNumber?: string | null;
  hasLinkedBoxes?: boolean;
  linkedWorkOrders?: InvoiceTrackingLineWorkOrderRef[];
  /** Distinct heat numbers from material_issue across all linked WOs on this line. */
  materialIssueHeatNumbers?: string[];
  boxes?: InvoiceTrackingBox[];
  invoiceLineHeats?: string[];
}

export interface InvoiceTrackingSummary {
  id?: number | null;
  invoiceNo?: string | null;
  invoiceDate?: string | null;
  status?: string | null;
  branchId?: number | null;
  branchName?: string | null;
  billToCustomerId?: number | null;
  billToCustomerName?: string | null;
  shipToCustomerId?: number | null;
  shipToCustomerName?: string | null;
  salesOrderNumber?: string | null;
  grandTotal?: number | null;
  irn?: string | null;
  ewayBillNo?: string | null;
  supplyType?: string | null;
}

export interface InvoiceTrackingDto {
  found: boolean;
  message?: string | null;
  invoice?: InvoiceTrackingSummary | null;
  hasLinkedBoxes?: boolean;
  lines?: InvoiceTrackingLine[];
  workOrders?: InvoiceTrackingWorkOrder[];
}

export interface ProcessedTrackingData {
  heatDetails: HeatDetails[];
  workOrderDetails: WorkOrderDetails[];
  boxDetails: BoxDetails[];
}

// Tracking API Service
class TrackingApiService extends BaseApiService {
  
  // Get tracking by heat number - returns flat array
  async getTrackingByHeatNumber(heatNo: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_HEAT_NUMBER(heatNo),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }

  // Get tracking by box number - returns flat array
  async getTrackingByBoxNumber(boxNumber: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_BOX_NUMBER(boxNumber),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }

  // Get tracking by work order number - returns flat array
  async getTrackingByWorkOrderNumber(workOrderNumber: string): Promise<RawTrackingRecord[]> {
    try {
      const data = await this.makeRequest<any>(
        API_ENDPOINTS.TRACKING.BY_WORK_ORDER_NUMBER(workOrderNumber),
        { method: HTTP_METHODS.GET }
      );
      // Handle different response formats
      if (Array.isArray(data)) {
        return data;
      }
      // If response is wrapped in a data property
      if (data && Array.isArray(data.data)) {
        return data.data;
      }
      // Return empty array if data is not in expected format
      return [];
    } catch (error) {
      toast.error('Failed to fetch tracking data');
      throw error;
    }
  }

  /** Invoice tracking by invoice number + branch (structured response). */
  async getTrackingByInvoiceNumber(
    invoiceNo: string,
    branchId: number
  ): Promise<ApiResponse<InvoiceTrackingDto>> {
    try {
      return await this.makeRequest<ApiResponse<InvoiceTrackingDto>>(
        API_ENDPOINTS.TRACKING.BY_INVOICE_NUMBER(invoiceNo, branchId),
        { method: HTTP_METHODS.GET }
      );
    } catch (error: any) {
      // Backend returns 404 when invoice is not found — surface clean message
      if (error?.status === 404) {
        const message =
          typeof error?.message === 'string' && error.message.includes(' - ')
            ? error.message.split(' - ').slice(1).join(' - ').trim()
            : 'Invoice not found';
        return {
          success: false,
          message: message || 'Invoice not found',
          data: {
            found: false,
            message: message || 'Invoice not found',
            hasLinkedBoxes: false,
            lines: [],
            workOrders: [],
          },
        };
      }
      toast.error('Failed to fetch invoice tracking');
      throw error;
    }
  }
}

export const trackingApiService = new TrackingApiService();
